import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { profilesService } from '../services/supabase';
import {
  ACCENT_PRESETS,
  DEFAULT_ACCENT_ID,
  applyAccentToDocument,
  getAccentPreset,
  type AccentId,
} from '../styles/accentPresets';
import {
  CALENDAR_EVENT_TYPE_IDS,
  DEFAULT_EVENT_TYPE_COLORS,
  mergeEventTypeColors,
  parseEventTypeColorOverrides,
  type CalendarEventTypeId,
} from '../styles/calendarEventTypeColors';

export interface AppearancePreferences {
  accentId: AccentId;
  /** Overrides only — missing keys use defaults */
  eventTypeColors: Partial<Record<CalendarEventTypeId, string>>;
}

interface AppearanceContextValue {
  appearance: AppearancePreferences;
  accentId: AccentId;
  setAccentId: (id: AccentId) => void;
  /** Resolved colors (defaults + overrides) */
  eventTypeColors: Record<CalendarEventTypeId, string>;
  setEventTypeColor: (type: CalendarEventTypeId, hex: string) => void;
  presets: typeof ACCENT_PRESETS;
}

const AppearanceContext = createContext<AppearanceContextValue | undefined>(undefined);

function storageKey(userId: string | undefined): string {
  return `leadsme:appearance:${userId || 'anon'}`;
}

function isValidAccentId(id: unknown): id is AccentId {
  return typeof id === 'string' && ACCENT_PRESETS.some((p) => p.id === id);
}

function readStoredAppearance(userId: string | undefined): AppearancePreferences {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) {
      return { accentId: DEFAULT_ACCENT_ID, eventTypeColors: {} };
    }
    const parsed = JSON.parse(raw) as Partial<AppearancePreferences>;
    return {
      accentId: isValidAccentId(parsed.accentId) ? parsed.accentId : DEFAULT_ACCENT_ID,
      eventTypeColors: parseEventTypeColorOverrides(parsed.eventTypeColors),
    };
  } catch {
    return { accentId: DEFAULT_ACCENT_ID, eventTypeColors: {} };
  }
}

function writeStoredAppearance(userId: string | undefined, prefs: AppearancePreferences): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

function isAppRoute(pathname: string): boolean {
  return pathname === '/app' || pathname.startsWith('/app/');
}

function prefsFromProfile(raw: unknown): AppearancePreferences {
  const obj =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return {
    accentId: isValidAccentId(obj.accentId) ? obj.accentId : DEFAULT_ACCENT_ID,
    eventTypeColors: parseEventTypeColorOverrides(obj.eventTypeColors),
  };
}

export const AppearanceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const [prefs, setPrefs] = useState<AppearancePreferences>(() => readStoredAppearance(undefined));

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!userId) {
        setPrefs(readStoredAppearance(undefined));
        return;
      }

      const cached = readStoredAppearance(userId);
      setPrefs(cached);

      try {
        const profile = await profilesService.getProfile();
        if (cancelled) return;

        const fromDb = prefsFromProfile(profile?.preferences);
        const hasAccentInDb =
          profile?.preferences &&
          typeof profile.preferences === 'object' &&
          isValidAccentId((profile.preferences as Record<string, unknown>).accentId);

        const next: AppearancePreferences = {
          accentId: hasAccentInDb ? fromDb.accentId : cached.accentId,
          eventTypeColors: {
            ...cached.eventTypeColors,
            ...fromDb.eventTypeColors,
          },
        };

        setPrefs(next);
        writeStoredAppearance(userId, next);

        if (!hasAccentInDb) {
          await profilesService.mergePreferences({
            accentId: next.accentId,
            eventTypeColors: next.eventTypeColors,
          });
        }
      } catch {
        /* keep cache */
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const persist = useCallback(
    (updater: (prev: AppearancePreferences) => AppearancePreferences) => {
      setPrefs((prev) => {
        const next = updater(prev);
        writeStoredAppearance(userId, next);
        if (userId) {
          void profilesService.mergePreferences({
            accentId: next.accentId,
            eventTypeColors: next.eventTypeColors,
          });
        }
        return next;
      });
    },
    [userId]
  );

  const setAccentId = useCallback(
    (id: AccentId) => {
      if (!isValidAccentId(id)) return;
      persist((prev) => ({ ...prev, accentId: id }));
    },
    [persist]
  );

  const setEventTypeColor = useCallback(
    (type: CalendarEventTypeId, hex: string) => {
      if (!CALENDAR_EVENT_TYPE_IDS.includes(type)) return;
      persist((prev) => {
        const nextOverrides = { ...prev.eventTypeColors };
        if (hex === DEFAULT_EVENT_TYPE_COLORS[type]) {
          delete nextOverrides[type];
        } else {
          nextOverrides[type] = hex;
        }
        return { ...prev, eventTypeColors: nextOverrides };
      });
    },
    [persist]
  );

  const eventTypeColors = useMemo(
    () => mergeEventTypeColors(prefs.eventTypeColors),
    [prefs.eventTypeColors]
  );

  const value = useMemo<AppearanceContextValue>(
    () => ({
      appearance: prefs,
      accentId: prefs.accentId,
      setAccentId,
      eventTypeColors,
      setEventTypeColor,
      presets: ACCENT_PRESETS,
    }),
    [prefs, setAccentId, eventTypeColors, setEventTypeColor]
  );

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
};

export const AppearanceRouteSync: React.FC = () => {
  const { pathname } = useLocation();
  const { accentId } = useAppearance();

  useEffect(() => {
    const preset = isAppRoute(pathname)
      ? getAccentPreset(accentId)
      : getAccentPreset(DEFAULT_ACCENT_ID);
    applyAccentToDocument(preset);
  }, [pathname, accentId]);

  return null;
};

export function useAppearance(): AppearanceContextValue {
  const ctx = useContext(AppearanceContext);
  if (!ctx) {
    throw new Error('useAppearance must be used within an AppearanceProvider');
  }
  return ctx;
}
